import {
  ARTIFACTS_STORAGE_KEY,
  DocumentIdentifier,
  DOCUMENTS_STORAGE_KEY,
  useCompositeDocumentStorageService,
  useCompositeDocumentStorageContext,
} from 'domain/shared/documentStorage';
import { useEffect, useMemo, useState } from 'react';
import { unFlattenObject, useInputHelper } from 'framework/form';
import { ErrorMessage } from 'framework/ui/layout';
import { normalizeStoragePath } from 'domain/shared/documentStorage';
import {
  BaseFileUploader,
  FileUploadLayout,
  useArtifactInputHelper,
  useArtifactValidationService,
} from 'domain/shared/artifact/input';
import CompositeDocumentPreviewListLayout from 'domain/shared/documentStorage/composite/CompositeDocumentPreviewListLayout';

const concatErrors = (errors1: null[], errors2: Nullable<string[]>) => {
  const toArr = (errors: null[] | Nullable<string[]>) => errors || [];
  return [...toArr(errors1), ...toArr(errors2)].filter(v => v);
};

interface DocumentUploaderInputProps {
  artifactsListSource: string;
  documentsListsSource: string;
  label?: string;

  canAddFromClipboard?: boolean;
  showBtnImage?: boolean;
  uploadBtnText?: string;

  artifactTypeId?: number;
  fileExtensions?: string;
  maximumSize?: number;
  maxElements?: number;
  addArtifactToContext?: (id: string, file: File) => void;
  removeArtifactFromContext?: (id: string) => void;
}

const CompositeDocumentUploaderInput = ({
  artifactsListSource,
  documentsListsSource,
  canAddFromClipboard = false,
  showBtnImage = true,
  uploadBtnText = 'Upload or drag files',
  ...props
}: DocumentUploaderInputProps) => {
  const { isDocumentStorageAvailable } = useCompositeDocumentStorageContext();
  const { uploadDocument } = useCompositeDocumentStorageService(isDocumentStorageAvailable);

  const [errorMsg, setErrorMsg] = useState(null);

  const {
    getLabel,
    getValue,
    getAcceptExtensions,
    addArtifactToContext,
    initializeValue,
    removeArtifactFromContext,
    getTooltip,
    getHelpText,
    getSource,
    getMaxElements,
    getArtifactType,
    getMaxMb,
    getIsRequired,
    getErrorMessages,
    getClearValidationCallback,
    isVisible,
    getIsDisabled,
  } = useArtifactInputHelper({
    ...props,
    source: isDocumentStorageAvailable ? documentsListsSource : artifactsListSource,
  });
  const clearValidation = getClearValidationCallback();

  const {
    getCollectionOnChangeCallback,
    getValue: getOldScenarioValue,
    getSource: getOldScenarioSource,
  } = useInputHelper({
    source: artifactsListSource,
  });

  const fullDocumentIdsList = useMemo(() => {
    const documentIdsList = getValue?.() || [];
    const artifactIdsList = getOldScenarioValue?.() || [];

    const documentsList = documentIdsList.map((item: string) => ({
      storage: isDocumentStorageAvailable ? DOCUMENTS_STORAGE_KEY : ARTIFACTS_STORAGE_KEY,
      id: item,
    }));

    const artifactsList = isDocumentStorageAvailable
      ? artifactIdsList.map((item: string) => ({
          storage: ARTIFACTS_STORAGE_KEY,
          id: item,
        }))
      : [];

    return [...artifactsList, ...documentsList];
  }, [getValue, getOldScenarioValue, isDocumentStorageAvailable]);

  /*
   * On create page input should have [] value by default.
   */
  useEffect(() => {
    if (!getValue()) {
      initializeValue([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { validate } = useArtifactValidationService({
    previewsCount: fullDocumentIdsList?.length,
    source: getSource(),
    maxMb: getMaxMb(),
    fileTypes: getAcceptExtensions(),
  });

  const isAllowedToAddMoreFiles =
    (!getMaxElements() && getMaxElements() !== 0) || getMaxElements() - fullDocumentIdsList.length > 0;

  const removeArtifact = (document: DocumentIdentifier) => {
    if (
      (document.storage === DOCUMENTS_STORAGE_KEY && isDocumentStorageAvailable) ||
      (document.storage === ARTIFACTS_STORAGE_KEY && !isDocumentStorageAvailable)
    ) {
      removeArtifactFromContext(document.id);
    } else if (document.storage === ARTIFACTS_STORAGE_KEY && isDocumentStorageAvailable) {
      getCollectionOnChangeCallback?.()((prev: string[]) => {
        const newValue = prev.filter(
          artifactId => normalizeStoragePath(artifactId) !== normalizeStoragePath(document.id),
        );

        return unFlattenObject({ [getOldScenarioSource?.()]: newValue });
      });
    }
  };

  return (
    <FileUploadLayout
      label={getLabel() as string}
      error={concatErrors([errorMsg], getErrorMessages()) as Nullable<ErrorMessage[]>}
      isRequired={getIsRequired()}
      tooltip={getTooltip() as string}
      source={getSource()}
      helpText={getHelpText()}
      disabled={getIsDisabled()}
      isVisible={isVisible()}
      previews={
        <CompositeDocumentPreviewListLayout
          artifactsIdsList={fullDocumentIdsList}
          isDocumentStorageAvailable={isDocumentStorageAvailable}
          onRemove={removeArtifact}
          disabled={getIsDisabled()}
        />
      }
      fileUploder={
        isAllowedToAddMoreFiles ? (
          <BaseFileUploader
            disabled={getIsDisabled()}
            canAddFromClipboard={canAddFromClipboard}
            artifactTypeId={getArtifactType()}
            uploadCallback={(id, file) => {
              addArtifactToContext(id, file);
              clearValidation();
            }}
            acceptExtensions={getAcceptExtensions()}
            validateFunc={validate}
            validationCallback={error => {
              setErrorMsg(error);
            }}
            source={getSource()}
            uploadFile={params => uploadDocument(params).then(uploaded => uploaded.id)}
            uploadBtnText={uploadBtnText}
            showBtnImage={showBtnImage}
            maxElements={getMaxElements()}
          />
        ) : null
      }
    />
  );
};

export default CompositeDocumentUploaderInput;
